/* Two profiling runs compared: the verdict, what to do about it, and every column measure by measure.
 *
 * **The verdict leads with what got worse**, because that is the question a reader arrives with, and
 * it names the two dataset measures in the same sentence when they fell — a dataset whose rows halved
 * is worse news than any one column, and a headline that said "nothing got worse" over it would be
 * wrong where it matters most.
 *
 * **The count tiles are the table's filter.** Each one lists its columns, so "3 worse" is a link to the
 * three rather than a number to go hunting for; a zero is greyed and inert, because a filter that
 * empties the table reads as a broken table.
 *
 * **Recommendations are the platform's, and the wording is ours.** The server decides which
 * degradations deserve one and how severe each is; this screen only words them, through the copy
 * catalogue. No customer value appears in any of them — a recommendation carries a column name and two
 * figures, nothing else.
 */

import { useMemo } from 'react';
import type { CSSProperties, KeyboardEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type {
  ColumnComparison,
  MetricComparison,
  ProfileComparison,
  ProfileRunSummary,
} from '@/shared/api/generated/catalog';
import { Button } from '@/shared/ui/Button';

import {
  COLUMN_METRICS,
  DATASET_METRICS,
  RECOMMENDATIONS_SHOWN,
  SEVERITY_GLYPH,
  SEVERITY_TONE,
  STATUS_ORDER,
  STATUS_TONE,
  dayLabel,
  daysApart,
  hasEstimate,
  headline,
  instantLabel,
  isHollow,
  metricOf,
  metricValue,
  movement,
  recommendationMetric,
  resultSegments,
  shareBar,
  sortColumns,
  visibleColumns,
} from '../model/history';
import type { ColumnFilter, ComparedMetric, ComparisonStatus } from '../model/history';
import {
  measureWord,
  metricLabel,
  movementText,
  recommendationText,
  severityLabel,
  statusLabel,
  statusMeaning,
  statusWord,
  valueOrNotMeasured,
} from './historyCopy';

export interface ComparisonViewProps {
  readonly comparison: ProfileComparison;
  readonly filter: ColumnFilter;
  readonly onFilter: (filter: ColumnFilter) => void;
  readonly query: string;
  readonly onQuery: (query: string) => void;
  readonly highlighted: string | null;
  readonly onShowColumn: (column: string) => void;
  readonly allRecommendations: boolean;
  readonly onAllRecommendations: () => void;
  /** Below 720px the table becomes one card per column. */
  readonly narrow: boolean;
  /** A newer pair is loading: the figures stay, dimmed, and cannot be acted on. */
  readonly busy: boolean;
}

export function ComparisonView({
  comparison,
  filter,
  onFilter,
  query,
  onQuery,
  highlighted,
  onShowColumn,
  allRecommendations,
  onAllRecommendations,
  narrow,
  busy,
}: ComparisonViewProps): ReactElement {
  const shown = useMemo(
    () => new Set(comparison.columns.map((column) => column.column)),
    [comparison.columns],
  );
  return (
    <>
      <Verdict comparison={comparison} filter={filter} onFilter={onFilter} busy={busy} />
      <section className="lq-ph__recs lq-ph__dim" aria-busy={busy}>
        <RecommendationsHead comparison={comparison} />
        <Recommendations
          comparison={comparison}
          canShow={(column) => shown.has(column)}
          onShowColumn={onShowColumn}
          all={allRecommendations}
          onAll={onAllRecommendations}
        />
      </section>
      <ColumnsCard
        comparison={comparison}
        filter={filter}
        onFilter={onFilter}
        query={query}
        onQuery={onQuery}
        highlighted={highlighted}
        narrow={narrow}
        busy={busy}
      />
    </>
  );
}

// ---------------------------------------------------------------------------------------- Verdict

function runReference(t: TFunction, run: ProfileRunSummary): string {
  const when = instantLabel(run.finished_at);
  return run.version_number === null
    ? when
    : t('profile.compare.runWithSchema', { when, version: run.version_number });
}

function headlineText(t: TFunction, comparison: ProfileComparison): string {
  const { degraded, fell } = headline(comparison.summary, comparison.dataset_metrics);
  const words = fell.map((metric) => measureWord(t, metric));
  const measures =
    words.length === 2
      ? t('profile.compare.measures.pair', { first: words[0], second: words[1] })
      : (words[0] ?? '');
  if (degraded > 0 && words.length > 0) {
    return t('profile.compare.headline.worseAndFell', { count: degraded, measures });
  }
  if (degraded > 0) return t('profile.compare.headline.worse', { count: degraded });
  if (words.length > 0) return t('profile.compare.headline.onlyFell', { measures });
  return t('profile.compare.headline.nothing');
}

function Verdict({
  comparison,
  filter,
  onFilter,
  busy,
}: {
  readonly comparison: ProfileComparison;
  readonly filter: ColumnFilter;
  readonly onFilter: (filter: ColumnFilter) => void;
  readonly busy: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { baseline: earlier, current: later, summary } = comparison;
  const verdict = headline(summary, comparison.dataset_metrics);
  const good = verdict.degraded === 0 && verdict.fell.length === 0;
  const segments = resultSegments(summary);
  const schemaChanged =
    earlier.version_number !== null &&
    later.version_number !== null &&
    earlier.version_number !== later.version_number;
  const gap = daysApart(earlier, later);

  return (
    <article className="lq-ph__verdict lq-ph__dim" aria-busy={busy}>
      <div className="lq-ph__verdictMain">
        <div className="lq-ph__verdictHead">
          <div className="lq-ph__spanRow">
            <span className="lq-ph__span">
              {t('profile.compare.span', {
                earlier: dayLabel(earlier.finished_at),
                later: dayLabel(later.finished_at),
              })}
            </span>
            {schemaChanged && (
              <span className="lq-ph__schemaChip">
                {t('profile.compare.schemaChanged', {
                  earlier: earlier.version_number,
                  later: later.version_number,
                })}
              </span>
            )}
          </div>
          <h2
            className={good ? 'lq-ph__headline lq-ph__headline--good' : 'lq-ph__headline'}
            title={t('profile.compare.computedFrom', { how: comparison.computed_from })}
          >
            {headlineText(t, comparison)}
          </h2>
          <p className="lq-ph__verdictSub">
            {t('profile.compare.sub', {
              earlier: runReference(t, earlier),
              later: runReference(t, later),
              gap: t('profile.compare.gap', { days: gap }),
            })}
          </p>
        </div>
        {segments.length > 0 && (
          <div
            className="lq-ph__segments"
            role="img"
            aria-label={t('profile.compare.summaryLabel')}
          >
            {segments.map((segment) => (
              <div
                key={segment.status}
                className="lq-ph__segment"
                data-status={segment.status}
                title={t('profile.compare.segmentTitle', {
                  count: segment.count,
                  status: statusWord(t, segment.status),
                })}
                style={{ '--ph-share': `${segment.share.toFixed(2)}%` } as CSSProperties}
              />
            ))}
          </div>
        )}
        <div className="lq-ph__tiles">
          {STATUS_ORDER.map((status) => {
            const count = summary[status];
            const on = filter === status;
            return (
              <button
                key={status}
                type="button"
                className="lq-ph__tile"
                data-status={status}
                aria-pressed={on}
                disabled={count === 0}
                title={
                  count === 0
                    ? statusMeaning(t, status)
                    : t('profile.compare.tileTitle', {
                        meaning: statusMeaning(t, status),
                      })
                }
                onClick={() => {
                  onFilter(on ? 'all' : status);
                }}
              >
                <span className="lq-ph__tileCount">{count}</span>
                <span className="lq-ph__tileLabel">
                  <span
                    className="lq-ph__swatch"
                    data-status={status}
                    aria-hidden="true"
                  />
                  {statusLabel(t, status)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="lq-ph__dataset">
        {DATASET_METRICS.map((name) => {
          const metric = comparison.dataset_metrics.find((each) => each.metric === name);
          return metric === undefined ? null : (
            <DatasetMeasure key={name} metric={metric} />
          );
        })}
      </div>
    </article>
  );
}

function StatusChip({ status }: { readonly status: ComparisonStatus }): ReactElement {
  const { t } = useTranslation();
  const tone = STATUS_TONE[status];
  return (
    <span className="lq-cn__state lq-pp__chip" data-tone={tone ?? undefined}>
      <span
        className={isHollow(status) ? 'lq-cn__dot lq-cn__dot--hollow' : 'lq-cn__dot'}
        aria-hidden="true"
      />
      {statusLabel(t, status)}
    </span>
  );
}

function DatasetMeasure({ metric }: { readonly metric: MetricComparison }): ReactElement {
  const { t } = useTranslation();
  const gauge =
    metric.metric === 'baseline' && metric.before !== null && metric.after !== null
      ? {
          after: Math.min(100, Math.max(0, metric.after)),
          before: Math.min(100, Math.max(0, metric.before)),
        }
      : null;
  return (
    <div className="lq-ph__measure" title={statusMeaning(t, metric.status)}>
      <div className="lq-ph__measureHead">
        <span className="lq-ph__measureLabel">{metricLabel(t, metric.metric)}</span>
        <StatusChip status={metric.status} />
      </div>
      <div className="lq-ph__figures">
        <span className="lq-ph__before">
          {valueOrNotMeasured(t, metricValue(metric.metric, metric.before))}
        </span>
        <span className="lq-ph__arrowSoft" aria-hidden="true">
          →
        </span>
        <span className="lq-ph__after">
          {valueOrNotMeasured(t, metricValue(metric.metric, metric.after))}
        </span>
        <span className="lq-ph__measureMove" data-status={metric.status}>
          {movementText(t, movement(metric))}
        </span>
      </div>
      {gauge !== null && (
        <div className="lq-ph__gauge" aria-hidden="true">
          <div
            className="lq-ph__gaugeFill"
            data-status={metric.status}
            style={{ '--ph-after': `${gauge.after.toFixed(1)}%` } as CSSProperties}
          />
          <div
            className="lq-ph__gaugeTick"
            title={t('profile.compare.earlierTick')}
            style={{ '--ph-before': `${gauge.before.toFixed(1)}%` } as CSSProperties}
          />
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------------- Recommendations

function RecommendationsHead({
  comparison,
}: {
  readonly comparison: ProfileComparison | null;
}): ReactElement {
  const { t } = useTranslation();
  const all = comparison?.recommendations ?? [];
  const high = all.filter((each) => each.severity === 'high').length;
  return (
    <div className="lq-ph__recsHead">
      <h2 className="lq-ph__sectionTitle">{t('profile.recommendations.title')}</h2>
      {all.length > 0 && (
        <span className="lq-ph__recsMeta">
          {high > 0
            ? t('profile.recommendations.summaryHigh', { count: all.length, high })
            : t('profile.recommendations.summary', { count: all.length })}
        </span>
      )}
      <span className="lq-ph__recsOrder">{t('profile.recommendations.order')}</span>
    </div>
  );
}

/** The recommendations section when no comparison can be shown, saying why there is none: a failed
 * comparison assessed nothing, which must not read as "nothing to act on". */
export function RecommendationsBlocked({
  failed,
}: {
  readonly failed: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <section className="lq-ph__recs">
      <RecommendationsHead comparison={null} />
      <p
        className={
          failed ? 'lq-ph__recsBlocked lq-ph__recsBlocked--failed' : 'lq-ph__recsBlocked'
        }
      >
        {failed
          ? t('profile.recommendations.failed')
          : t('profile.recommendations.waiting')}
      </p>
    </section>
  );
}

function figureLabel(t: TFunction, metric: ComparedMetric): string {
  return metricLabel(t, metric);
}

function Recommendations({
  comparison,
  canShow,
  onShowColumn,
  all,
  onAll,
}: {
  readonly comparison: ProfileComparison;
  readonly canShow: (column: string) => boolean;
  readonly onShowColumn: (column: string) => void;
  readonly all: boolean;
  readonly onAll: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const recommendations = comparison.recommendations;
  if (recommendations.length === 0) {
    return (
      <p className="lq-ph__nothing">
        <span className="lq-ph__check" aria-hidden="true">
          ✓
        </span>
        <span>
          <strong className="lq-ph__nothingTitle">
            {t('profile.recommendations.none.title')}
          </strong>{' '}
          {t('profile.recommendations.none.body')}
        </span>
      </p>
    );
  }
  const visible = all ? recommendations : recommendations.slice(0, RECOMMENDATIONS_SHOWN);
  const more = recommendations.length - RECOMMENDATIONS_SHOWN;
  return (
    <>
      <ul className="lq-ph__recGrid">
        {visible.map((recommendation) => {
          const metric = recommendationMetric(recommendation);
          const tone = SEVERITY_TONE[recommendation.severity];
          const column = recommendation.column;
          return (
            <li key={`${recommendation.kind}:${column ?? ''}`} className="lq-ph__rec">
              <div className="lq-ph__recHead">
                <span className="lq-ph__sev" data-tone={tone}>
                  <span className="lq-ph__sevGlyph" aria-hidden="true">
                    {SEVERITY_GLYPH[recommendation.severity]}
                  </span>
                  {severityLabel(t, recommendation.severity)}
                </span>
                <span
                  className={
                    column === null
                      ? 'lq-ph__recSubject'
                      : 'lq-ph__recSubject lq-ph__mono'
                  }
                  title={column ?? undefined}
                >
                  {column ?? t('profile.recommendations.dataset')}
                </span>
              </div>
              {metric !== null &&
              recommendation.before !== null &&
              recommendation.after !== null ? (
                <div className="lq-ph__recFigure">
                  <span className="lq-ph__recFigureLabel">{figureLabel(t, metric)}</span>
                  <span className="lq-ph__recBefore">
                    {metricValue(metric, recommendation.before)}
                  </span>
                  <span className="lq-ph__arrowSoft" aria-hidden="true">
                    →
                  </span>
                  <span className="lq-ph__recAfter" data-tone={tone}>
                    {metricValue(metric, recommendation.after)}
                  </span>
                </div>
              ) : recommendation.kind === 'column_dropped' ? (
                <div className="lq-ph__recFigureLabel">
                  {t('profile.recommendations.dropped')}
                </div>
              ) : null}
              <p className="lq-ph__recText">{recommendationText(t, recommendation)}</p>
              {column !== null && canShow(column) && (
                <button
                  type="button"
                  className="lq-ph__link"
                  onClick={() => {
                    onShowColumn(column);
                  }}
                >
                  {t('profile.recommendations.show')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {!all && more > 0 && (
        <div>
          <Button size="compact" onClick={onAll}>
            {t('profile.recommendations.more', { count: more })}
          </Button>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------- Columns

/** A measure the server did not send for a column reads as not comparable rather than vanishing, so
 * the table's three columns always line up. */
function measureOf(column: ColumnComparison, name: ComparedMetric): MetricComparison {
  return (
    metricOf(column, name) ?? {
      metric: name,
      before: null,
      after: null,
      change: null,
      status: 'not_comparable',
      exact: true,
    }
  );
}

interface CellReading {
  readonly label: string;
  readonly fromTo: string;
  readonly move: string;
  readonly title: string;
  readonly status: ComparisonStatus;
  readonly bar: { readonly after: number; readonly before: number } | null;
}

function cellReading(t: TFunction, metric: MetricComparison): CellReading {
  const label = metricLabel(t, metric.metric);
  const before = valueOrNotMeasured(
    t,
    metricValue(metric.metric, metric.before, metric.exact),
  );
  const after = valueOrNotMeasured(
    t,
    metricValue(metric.metric, metric.after, metric.exact),
  );
  const move = movement(metric);
  const moveText = movementText(t, move);
  const word =
    metric.status === 'not_comparable'
      ? moveText
      : metric.status === 'unchanged' && move.kind === 'none'
        ? moveText
        : t('profile.compare.cell', {
            move: moveText,
            status: statusWord(t, metric.status),
          });
  const estimated = metric.metric === 'distinct_count' && !metric.exact;
  const title = [
    t('profile.compare.cellTitle', {
      measure: label,
      status: statusLabel(t, metric.status),
      meaning: statusMeaning(t, metric.status),
    }),
    estimated ? t('profile.compare.estimatedNote') : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' ');
  return {
    label,
    fromTo: t('profile.compare.fromTo', { before, after }),
    move: word,
    title,
    status: metric.status,
    bar: metric.status === 'not_comparable' ? null : shareBar(metric),
  };
}

function ColumnsCard({
  comparison,
  filter,
  onFilter,
  query,
  onQuery,
  highlighted,
  narrow,
  busy,
}: {
  readonly comparison: ProfileComparison;
  readonly filter: ColumnFilter;
  readonly onFilter: (filter: ColumnFilter) => void;
  readonly query: string;
  readonly onQuery: (query: string) => void;
  readonly highlighted: string | null;
  readonly narrow: boolean;
  readonly busy: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const sorted = useMemo(() => sortColumns(comparison.columns), [comparison.columns]);
  const total = sorted.length;
  const { rows, hidden } = visibleColumns(sorted, filter, query);
  const needs = sorted.filter((column) => column.status !== 'unchanged').length;
  const views = [
    { id: 'attention', label: t('profile.compare.columns.attention', { count: needs }) },
    { id: 'all', label: t('profile.compare.columns.all', { count: total }) },
  ] as const;
  const viewOn = filter === 'attention' ? 'attention' : filter === 'all' ? 'all' : null;
  const onViewKey = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    onFilter(viewOn === 'attention' ? 'all' : 'attention');
  };

  return (
    <article className="lq-ph__cols lq-ph__dim" aria-busy={busy}>
      <div className="lq-ph__colsHead">
        <div className="lq-ph__colsTitles">
          <h2 className="lq-ph__sectionTitle">{t('profile.compare.columns.title')}</h2>
          {total > 0 && (
            <span className="lq-ph__sub">
              {t('profile.compare.columns.caption', { count: total })}
            </span>
          )}
        </div>
        {filter !== 'all' && filter !== 'attention' && (
          <button
            type="button"
            className="lq-ph__filterChip"
            aria-label={t('profile.compare.filterClear', {
              status: statusWord(t, filter),
            })}
            onClick={() => {
              onFilter('all');
            }}
          >
            {t('profile.compare.filterOnly', { status: statusWord(t, filter) })}
            <span aria-hidden="true">✕</span>
          </button>
        )}
        {total > 12 && (
          <input
            type="search"
            className="lq-ph__search"
            value={query}
            placeholder={t('profile.compare.columns.search')}
            aria-label={t('profile.compare.columns.search')}
            onChange={(event) => {
              onQuery(event.target.value);
            }}
          />
        )}
        {total > 0 && (
          <div
            className="lq-ph__views"
            role="radiogroup"
            aria-label={t('profile.compare.columns.which')}
          >
            {views.map((view) => {
              const on = viewOn === view.id;
              return (
                <button
                  key={view.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  tabIndex={on || (viewOn === null && view.id === 'all') ? 0 : -1}
                  className="lq-ph__view"
                  onKeyDown={onViewKey}
                  onClick={() => {
                    onFilter(view.id);
                  }}
                >
                  {view.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {total === 0 && (
        <div className="lq-ph__noShared">
          <p className="lq-ph__noSharedTitle">{t('profile.compare.noShared.title')}</p>
          <p className="lq-ph__noSharedText">{t('profile.compare.noShared.body')}</p>
        </div>
      )}

      {rows.length > 0 &&
        (narrow ? (
          <ul
            className="lq-ph__cards"
            aria-label={t('profile.compare.columns.tableLabel')}
          >
            {rows.map((column) => (
              <li
                key={column.column}
                data-ph-col={column.column}
                className={
                  highlighted === column.column
                    ? 'lq-ph__card lq-ph__card--hl'
                    : 'lq-ph__card'
                }
              >
                <div className="lq-ph__cardHead">
                  <span className="lq-ph__cardName lq-ph__mono">{column.column}</span>
                  <StatusChip status={column.status} />
                </div>
                <dl className="lq-ph__dl">
                  {COLUMN_METRICS.map((name) => {
                    const cell = cellReading(t, measureOf(column, name));
                    return (
                      <div key={name} className="lq-ph__dlRow" title={cell.title}>
                        <dt>{cell.label}</dt>
                        <dd>
                          <span
                            className={
                              cell.status === 'not_comparable'
                                ? 'lq-ph__fromTo lq-ph__fromTo--nc'
                                : 'lq-ph__fromTo'
                            }
                          >
                            {cell.fromTo}
                          </span>
                          <span className="lq-ph__move" data-status={cell.status}>
                            {cell.move}
                          </span>
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </li>
            ))}
          </ul>
        ) : (
          <div
            className="lq-ph__table"
            role="table"
            aria-label={t('profile.compare.columns.tableLabel')}
          >
            <div className="lq-ph__row lq-ph__row--head" role="row">
              <span role="columnheader">{t('profile.compare.col.column')}</span>
              {COLUMN_METRICS.map((name) => (
                <span key={name} role="columnheader">
                  {metricLabel(t, name)}
                </span>
              ))}
              <span role="columnheader">{t('profile.compare.col.result')}</span>
            </div>
            {rows.map((column) => (
              <div
                key={column.column}
                role="row"
                data-ph-col={column.column}
                className={
                  highlighted === column.column
                    ? 'lq-ph__row lq-ph__row--hl'
                    : 'lq-ph__row'
                }
              >
                <span
                  role="cell"
                  className="lq-ph__colName lq-ph__mono"
                  title={column.column}
                >
                  {column.column}
                </span>
                {COLUMN_METRICS.map((name) => {
                  const cell = cellReading(t, measureOf(column, name));
                  return (
                    <div
                      key={name}
                      role="cell"
                      className="lq-ph__cell"
                      title={cell.title}
                    >
                      <span
                        className={
                          cell.status === 'not_comparable'
                            ? 'lq-ph__fromTo lq-ph__fromTo--nc'
                            : 'lq-ph__fromTo'
                        }
                      >
                        {cell.fromTo}
                      </span>
                      {cell.bar !== null && (
                        <span className="lq-ph__bar" aria-hidden="true">
                          <span
                            className="lq-ph__barFill"
                            data-status={cell.status}
                            style={
                              {
                                '--ph-after': `${cell.bar.after.toFixed(1)}%`,
                              } as CSSProperties
                            }
                          />
                          <span
                            className="lq-ph__barTick"
                            style={
                              {
                                '--ph-before': `${cell.bar.before.toFixed(1)}%`,
                              } as CSSProperties
                            }
                          />
                        </span>
                      )}
                      <span className="lq-ph__move" data-status={cell.status}>
                        {cell.move}
                      </span>
                    </div>
                  );
                })}
                <span role="cell">
                  <StatusChip status={column.status} />
                </span>
              </div>
            ))}
          </div>
        ))}

      {hidden > 0 && (
        <button
          type="button"
          className="lq-ph__showHidden"
          onClick={() => {
            onFilter('all');
          }}
        >
          {t('profile.compare.columns.showUnchanged', { count: hidden })}
        </button>
      )}
      {rows.length === 0 && total > 0 && (
        <p className="lq-ph__noMatch">
          {query.trim() === ''
            ? t('profile.compare.columns.noneInGroup')
            : t('profile.compare.columns.noMatch', { query: query.trim() })}
        </p>
      )}

      <div className="lq-ph__colsFoot">
        {comparison.added_columns.length > 0 && (
          <p className="lq-ph__only">
            {t('profile.compare.addedLabel')}{' '}
            <span className="lq-ph__mono">{comparison.added_columns.join(', ')}</span>
          </p>
        )}
        {comparison.removed_columns.length > 0 && (
          <p className="lq-ph__only">
            {t('profile.compare.removedLabel')}{' '}
            <span className="lq-ph__mono">{comparison.removed_columns.join(', ')}</span>
          </p>
        )}
        {total > 0 && (
          <p className="lq-ph__legend">
            <span className="lq-ph__legendItem">
              <span className="lq-ph__legendBar" aria-hidden="true">
                <span className="lq-ph__legendTick" />
              </span>
              {t('profile.compare.legend.bar')}
            </span>
            <span className="lq-ph__legendItem">
              {t('profile.compare.legend.points')}
            </span>
            {hasEstimate(comparison.columns) && (
              <span className="lq-ph__legendItem">
                {t('profile.compare.legend.estimated')}
              </span>
            )}
          </p>
        )}
      </div>
    </article>
  );
}
